<template>
  <div class="device-center-container">
    <!-- 头部操作栏 -->
    <div class="header-section">
      <div class="header-content">
        <h2 class="page-title">{{ $t('device.title') }}</h2>
        <p class="page-subtitle">{{ $t('device.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button @click="showLogDialog" class="log-btn">
          <span class="btn-icon">📋</span>
          <span class="btn-text">{{ $t('device.viewLogs') }}</span>
        </button>
        <button @click="showAddDialog" class="add-btn">
          <span class="btn-icon">+</span>
          <span class="btn-text">{{ $t('device.addDevice') }}</span>
        </button>
      </div>
    </div>

    <!-- 设备统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card total">
        <div class="stat-icon">🖥️</div>
        <div class="stat-content">
          <h3 class="stat-title">{{ $t('device.totalDevices') }}</h3>
          <p class="stat-value">{{ deviceList.length }}</p>
        </div>
      </div>
      <div class="stat-card online">
        <div class="stat-icon">🟢</div>
        <div class="stat-content">
          <h3 class="stat-title">{{ $t('device.onlineDevices') }}</h3>
          <p class="stat-value">{{ onlineCount }}</p>
        </div>
      </div>
      <div class="stat-card offline">
        <div class="stat-icon">🔴</div>
        <div class="stat-content">
          <h3 class="stat-title">{{ $t('device.offlineDevices') }}</h3>
          <p class="stat-value">{{ offlineCount }}</p>
        </div>
      </div>
    </div>

    <!-- 设备列表 -->
    <div class="device-list-section">
      <div class="section-header">
        <h3 class="section-title">{{ $t('device.deviceList') }}</h3>
        <div class="search-box">
          <input
            type="text"
            v-model="searchKeyword"
            :placeholder="$t('device.searchPlaceholder')"
            class="search-input"
          />
          <span class="search-icon">🔍</span>
        </div>
      </div>

      <div v-if="filteredDevices.length === 0" class="empty-state">
        <div class="empty-icon">🖥️</div>
        <h4 class="empty-title">{{ $t('device.noDevices') }}</h4>
        <p class="empty-desc">{{ $t('device.noDevicesHint') }}</p>
        <button @click="showAddDialog" class="empty-action-btn">
          {{ $t('device.addDevice') }}
        </button>
      </div>

      <div v-else class="device-grid">
        <div 
          v-for="device in filteredDevices" 
          :key="device.index" 
          class="device-card"
          :class="{ 'online': device.isOnline, 'offline': !device.isOnline }"
        >
          <div class="device-card-header">
            <div class="device-status">
              <span class="status-dot" :class="{ 'online': device.isOnline, 'offline': !device.isOnline }"></span>
              <span class="status-text">{{ device.isOnline ? $t('device.online') : $t('device.offline') }}</span>
            </div>
            <div class="device-actions">
              <button @click.stop="showEditDialog(device)" class="icon-btn edit-btn" :title="$t('device.edit')">
                ✏️
              </button>
              <button @click.stop="confirmDelete(device)" class="icon-btn delete-btn" :title="$t('device.delete')">
                🗑️
              </button>
            </div>
          </div>

          <div class="device-info">
            <h4 class="device-name">{{ device.name }}</h4>
            <p class="device-addr">
              <span class="info-label">{{ $t('device.address') }}：</span>
              <span class="info-value">{{ device.addr }}</span>
            </p>
            <p class="device-securePath">
              <span class="info-label">{{ $t('device.securePath') }}：</span>
              <span class="info-value">{{ device.securePath }}</span>
            </p>
            <p class="device-securePath">
              <span class="info-label">{{ $t('device.remark') }}：</span>
              <span class="info-value">{{ device.remark }}</span>
            </p>
          </div>

          <div class="device-footer">
            <div class="device-index">
              <span class="index-label">{{ $t('device.index') }}：</span>
              <span class="index-value">#{{ device.index }}</span>
            </div>

            <button
              @click.stop="ConnectDevice(device)"
              class="connect-btn"
            >
              {{ $t('device.connectDevice') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑设备对话框 -->
    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="device-dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ isEditMode ? $t('device.editDeviceTitle') : $t('device.addDeviceTitle') }}</h3>
          <button @click="closeDialog" class="close-btn">×</button>
        </div>

        <div class="dialog-content">
          <div class="form-group">
            <label for="deviceName">{{ $t('device.deviceNameLabel') }}</label>
            <input
              type="text"
              id="deviceName"
              v-model="currentDevice.name"
              :placeholder="$t('device.deviceNamePlaceholder')"
              class="form-input"
              :disabled="isEditMode"
              :class="{ 'error': errors.name }"
            />
            <div v-if="errors.name" class="error-message">{{ errors.name }}</div>
          </div>

          <div class="form-group">
            <label for="deviceAddr">{{ $t('device.deviceAddrLabel') }}</label>
            <input
              type="text"
              id="deviceAddr"
              v-model="currentDevice.addr"
              :placeholder="$t('device.deviceAddrPlaceholder')"
              class="form-input"
              :class="{ 'error': errors.addr }"
            />
            <div v-if="errors.addr" class="error-message">{{ errors.addr }}</div>
          </div>

          <div class="form-group">
            <label for="deviceSecurePath">{{ $t('device.securePathLabel') }}</label>
            <input
              type="text"
              id="deviceSecurePath"
              v-model="currentDevice.securePath"
              :placeholder="$t('device.securePathPlaceholder')"
              class="form-input"
              :class="{ 'error': errors.securePath }"
            />
            <div v-if="errors.securePath" class="error-message">{{ errors.securePath }}</div>
          </div>

          <div class="form-group">
            <label for="deviceRemark">{{ $t('device.remarkLabel') }}</label>
            <textarea
              id="deviceRemark"
              v-model="currentDevice.remark"
              :placeholder="$t('device.remarkPlaceholder')"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="dialog-actions">
          <button @click="closeDialog" class="btn cancel-btn">{{ $t('device.cancel') }}</button>
          <button
            @click="saveDevice"
            class="btn save-btn"
            :disabled="saving"
          >
            {{ saving ? $t('device.saving') : (isEditMode ? $t('device.updateDevice') : $t('device.addDeviceAction')) }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认对话框 -->
    <div v-if="showDeleteConfirm" class="dialog-overlay" @click="cancelDelete">
      <div class="confirm-dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ $t('device.confirmDelete') }}</h3>
        </div>
        <div class="dialog-content">
          <p>{{ $t('device.confirmDeleteMsg', { name: deviceToDelete?.name }) }}</p>
          <p class="warning-text">{{ $t('device.operationUndone') }}</p>
        </div>
        <div class="dialog-actions">
          <button @click="cancelDelete" class="btn cancel-btn">{{ $t('device.cancel') }}</button>
          <button
            @click="deleteDevice"
            class="btn delete-confirm-btn"
            :disabled="deleting"
          >
            {{ deleting ? $t('device.deleting') : $t('device.confirmDeleteAction') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 日志查看对话框 -->
    <div v-if="logDialogVisible" class="dialog-overlay" @click="closeLogDialog">
      <div class="log-dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ $t('device.operationLogs') }}</h3>
          <div class="log-header-actions">
            <div class="auto-refresh-control">
              <span class="refresh-label">{{ $t('device.autoRefresh') }}</span>
              <label class="switch">
                <input type="checkbox" v-model="autoRefreshLogEnabled" @change="toggleLogAutoRefresh">
                <span class="slider"></span>
              </label>
              <span class="refresh-interval" v-if="autoRefreshLogEnabled">{{ $t('device.refreshInterval') }}</span>
            </div>
            <button @click="closeLogDialog" class="close-btn">×</button>
          </div>
        </div>

        <div class="dialog-content">
          <div class="log-toolbar">
            <div class="log-stats">
              <span>{{ $t('device.totalLogs', { count: logTotal }) }}</span>
            </div>
            <div class="log-actions">
              <button
                @click="downloadAllLogs"
                class="log-action-btn download-btn"
                :disabled="logDownloading"
              >
                {{ logDownloading ? $t('device.downloading') : $t('device.downloadAllLogs') }}
              </button>
              <button
                @click="refreshLogs"
                class="log-action-btn refresh-btn"
                :disabled="logLoading"
              >
                {{ logLoading ? $t('device.refreshing') : $t('device.refreshNow') }}
              </button>
              <button
                @click="clearLogs"
                class="log-action-btn clear-btn"
                :disabled="logClearing"
              >
                {{ logClearing ? $t('device.clearing') : $t('device.clearLogs') }}
              </button>
            </div>
          </div>

          <div class="log-content" ref="logContentRef">
            <div v-if="logsData.length === 0" class="empty-log">
              <div class="empty-icon">📝</div>
              <p>{{ $t('device.noLogs') }}</p>
            </div>
            <div v-else class="log-list">
              <div
                v-for="(log, index) in logsData"
                :key="index"
                class="log-item"
                :class="`log-level-${log.Level.toLowerCase()}`"
              >
                <span class="log-time">{{ log.Time }}</span>
                <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
                  [{{ log.Level }}]
                </span>
                <span class="log-message">{{ log.Content }}</span>
              </div>
            </div>
          </div>

          <div class="log-pagination" v-if="logTotal > 0">
            <div class="pagination-controls">
              <button
                @click="changeLogPage(logCurrentPage - 1)"
                :disabled="logCurrentPage === 1"
                class="page-btn"
              >‹</button>
              <span class="page-info">{{ logCurrentPage }} / {{ totalPages }}</span>
              <button
                @click="changeLogPage(logCurrentPage + 1)"
                :disabled="logCurrentPage === totalPages"
                class="page-btn"
              >›</button>
              <select v-model="logPageSize" @change="handleLogSizeChange" class="page-size-select">
                <option value="20">{{ $t('device.logsPerPage', { count: 20 }) }}</option>
                <option value="50">{{ $t('device.logsPerPage', { count: 50 }) }}</option>
                <option value="100">{{ $t('device.logsPerPage', { count: 100 }) }}</option>
                <option value="200">{{ $t('device.logsPerPage', { count: 200 }) }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="dialog-actions">
          <button @click="closeLogDialog" class="btn cancel-btn">{{ $t('device.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { API_BASE_URL } from '@/config';
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default {
  name: 'DeviceCenter',
  setup() {
    const { t } = useI18n()
    const router = useRouter()

    // 状态管理
    const deviceList = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const deleting = ref(false);
    const testingDevice = ref(null);
    const searchKeyword = ref('');
    const showDialog = ref(false);
    const showDeleteConfirm = ref(false);
    
    // 定时器引用
    const refreshTimer = ref(null);
    const autoRefreshEnabled = ref(true);
    
    // 当前操作设备
    const currentDevice = ref({
      index: 0,
      name: '',
      addr: '',
      securePath: '',
      remark: ''
    });
    
    const deviceToDelete = ref(null);
    const isEditMode = ref(false);
    
    // 表单错误
    const errors = ref({
      name: '',
      addr: '',
      securePath: ''
    });

    // 日志相关
    const logDialogVisible = ref(false);
    const logLoading = ref(false);
    const logClearing = ref(false);
    const logDownloading = ref(false);
    const logsData = ref([]);
    const logTotal = ref(0);
    const logCurrentPage = ref(1);
    const logPageSize = ref(50);
    const logContentRef = ref(null);
    const autoRefreshLogEnabled = ref(true);
    const logRefreshInterval = ref(null);

    // 计算属性
    const filteredDevices = computed(() => {
      if (!searchKeyword.value.trim()) {
        return deviceList.value;
      }
      const keyword = searchKeyword.value.toLowerCase();
      return deviceList.value.filter(device => 
        device.name.toLowerCase().includes(keyword) ||
        device.addr.toLowerCase().includes(keyword) ||
        (device.remark && device.remark.toLowerCase().includes(keyword))
      );
    });

    const onlineCount = computed(() => {
      return deviceList.value.filter(device => device.isOnline).length;
    });

    const offlineCount = computed(() => {
      return deviceList.value.filter(device => !device.isOnline).length;
    });

    const totalPages = computed(() => {
      return Math.ceil(logTotal.value / logPageSize.value);
    });

    // API 调用
    const fetchDeviceList = async () => {
      loading.value = true;
      try {
        const response = await fetch(`${API_BASE_URL}/device-center`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.errCode === 1000) {
          // 未登录状态，跳转到登录页
          ElMessage.warning(t('device.loginRequired'));
          router.push('/user-login?callback=device-center');
          return;
        }

        if (data.errCode === 0) {
          deviceList.value = data.data || [];
        } else {
          ElMessage.error(data.errMsg || t('device.fetchDeviceListFailed'));
        }
      } catch (error) {
        console.error('fetchDeviceList error:', error);
        ElMessage.error(t('device.fetchDeviceListFailedRetry'));
      } finally {
        loading.value = false;
      }
    };

    // 启动自动刷新
    const startAutoRefresh = () => {
      // 清除现有定时器
      if (refreshTimer.value) {
        clearInterval(refreshTimer.value);
      }
      
      // 设置新的定时器，每5秒刷新一次
      refreshTimer.value = setInterval(() => {
        fetchDeviceList();
      }, 5000);
    };

    // 停止自动刷新
    const stopAutoRefresh = () => {
      if (refreshTimer.value) {
        clearInterval(refreshTimer.value);
        refreshTimer.value = null;
      }
    };

    const validateForm = () => {
      errors.value = {
        name: '',
        addr: '',
        securePath: ''
      };

      let isValid = true;

      if (!currentDevice.value.name.trim()) {
        errors.value.name = t('device.deviceNameRequired');
        isValid = false;
      }

      if (!currentDevice.value.addr.trim()) {
        errors.value.addr = t('device.deviceAddrRequired');
        isValid = false;
      }

      return isValid;
    };

    const showAddDialog = () => {
      currentDevice.value = {
        index: 0,
        name: '',
        addr: '',
        securePath: '',
        remark: ''
      };
      isEditMode.value = false;
      showDialog.value = true;
      errors.value = {};
    };

    const showEditDialog = (device) => {
      currentDevice.value = { ...device };
      isEditMode.value = true;
      showDialog.value = true;
      errors.value = {};
    };

    const closeDialog = () => {
      showDialog.value = false;
      currentDevice.value = {
        index: 0,
        name: '',
        addr: '',
        securePath: '',
        remark: ''
      };
      errors.value = {};
    };

    const saveDevice = async () => {
      if (!validateForm()) {
        return;
      }

      saving.value = true;
      try {
        const apiUrl = isEditMode.value
          ? `${API_BASE_URL}/update-device`
          : `${API_BASE_URL}/add-device`;

        const formData = new URLSearchParams();
        if (isEditMode.value) {
          formData.append('index', currentDevice.value.index);
        }
        formData.append('name', currentDevice.value.name);
        formData.append('addr', currentDevice.value.addr);
        formData.append('securePath', currentDevice.value.securePath);
        formData.append('remark', currentDevice.value.remark || '');

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
          credentials: 'include'
        });

        const data = await response.json();

        if (data.errCode === 0) {
          ElMessage.success(isEditMode.value ? t('device.deviceUpdateSuccess') : t('device.deviceAddSuccess'));
          closeDialog();
          fetchDeviceList();
        } else {
          ElMessage.error(data.errMsg || (isEditMode.value ? t('device.deviceUpdateFailed') : t('device.deviceAddFailed')));
        }
      } catch (error) {
        console.error('saveDevice error:', error);
        ElMessage.error(t('device.saveDeviceFailedRetry'));
      } finally {
        saving.value = false;
      }
    };

    const confirmDelete = (device) => {
      deviceToDelete.value = device;
      showDeleteConfirm.value = true;
    };

    const cancelDelete = () => {
      deviceToDelete.value = null;
      showDeleteConfirm.value = false;
    };

    const deleteDevice = async () => {
      if (!deviceToDelete.value) return;

      deleting.value = true;
      try {
        const formData = new URLSearchParams();
        formData.append('name', deviceToDelete.value.name);

        const response = await fetch(`${API_BASE_URL}/delete-device`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
          credentials: 'include'
        });

        const data = await response.json();

        if (data.errCode === 0) {
          ElMessage.success(t('device.deviceDeleteSuccess'));
          cancelDelete();
          fetchDeviceList();
        } else {
          ElMessage.error(data.errMsg || t('device.deviceDeleteFailed'));
        }
      } catch (error) {
        console.error('deleteDevice error:', error);
        ElMessage.error(t('device.deviceDeleteFailedRetry'));
      } finally {
        deleting.value = false;
      }
    };

    const ConnectDevice = async (device) => {
        try {
          const addr = device.addr;
          const securePath = device.securePath;

          // 检查地址是否有效
          if (!addr || addr.trim() === '') {
            ElMessage.error(t('device.deviceAddrEmpty'));
            return;
          }

          // 确保地址有协议前缀
          let fullAddr = addr;
          if (!fullAddr.startsWith('http://') && !fullAddr.startsWith('https://')) {
            fullAddr = 'http://' + fullAddr;
          }

          if (securePath != "") {
            fullAddr = fullAddr + "/user-login/" + securePath;
          }

          // 在新窗口打开设备地址
          const newWindow = window.open(fullAddr, '_blank');

          // 检查是否弹出窗口被阻止
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            ElMessage.warning(t('device.popupBlocked'));
            // 可选：提供复制地址到剪贴板的功能
            try {
              await navigator.clipboard.writeText(fullAddr);
              ElMessage.info(t('device.addressCopied', { address: fullAddr }));
            } catch (clipboardError) {
              console.error('copyAddress error:', clipboardError);
            }
          } else {
            ElMessage.success(t('device.openingDevicePage'));
          }

        } catch (error) {
          console.error('ConnectDevice error:', error);
          ElMessage.error(t('device.openDevicePageFailed', { error: error.message }));
        }
    };

    // 日志相关方法
    const showLogDialog = () => {
      logDialogVisible.value = true;
      fetchLogs();
      if (autoRefreshLogEnabled.value) {
        startLogAutoRefresh();
      }
    };

    const closeLogDialog = () => {
      logDialogVisible.value = false;
      stopLogAutoRefresh();
    };

    const toggleLogAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh();
        ElMessage.success(t('device.autoRefreshEnabled'));
      } else {
        stopLogAutoRefresh();
        ElMessage.info(t('device.autoRefreshDisabled'));
      }
    };

    const startLogAutoRefresh = () => {
      stopLogAutoRefresh();
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false);
        }
      }, 5000);
    };

    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value);
        logRefreshInterval.value = null;
      }
    };

    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true;
        }

        const params = new URLSearchParams({
          module: 'device',
          page: logCurrentPage.value,
          size: logPageSize.value
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('device.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('device.loginRequiredRedirect'));
          router.push('/user-login?callback=device-center');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('device.fetchLogsFailed'));
        }

        logsData.value = data.data || [];
        logTotal.value = data.count || 0;

        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0;
        }
      } catch (error) {
        console.error('fetchLogs error:', error);
        if (showLoading) {
          ElMessage.error(t('device.fetchLogsFailed') + ': ' + (error.message || 'Unknown error'));
        }
      } finally {
        if (showLoading) {
          logLoading.value = false;
        }
      }
    };

    const refreshLogs = () => {
      fetchLogs(true);
    };

    const clearLogs = async () => {
      if (!confirm(t('device.clearLogsConfirm'))) return;

      logClearing.value = true;
      try {
        const params = new URLSearchParams({
          module: 'device'
        });

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('device.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('device.loginRequiredRedirect'));
          router.push('/user-login?callback=device-center');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('device.fetchLogsFailed'));
        }

        ElMessage.success(t('device.logsCleared'));
        fetchLogs(true);
      } catch (error) {
        console.error('clearLogs error:', error);
        ElMessage.error(t('device.logsClearFailed', { error: error.message || 'Unknown error' }));
      } finally {
        logClearing.value = false;
      }
    };

    const downloadAllLogs = async () => {
      logDownloading.value = true;
      try {
        const params = new URLSearchParams({
          module: 'device',
          page: 1,
          size: 10000
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('device.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('device.loginRequiredRedirect'));
          router.push('/user-login?callback=device-center');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('device.fetchLogsFailed'));
        }

        const allLogs = data.data || [];

        if (allLogs.length === 0) {
          ElMessage.warning(t('device.noLogsToDownload'));
          return;
        }

        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });

        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `device_center_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        ElMessage.success(t('device.logsDownloadSuccess', { count: allLogs.length }));
      } catch (error) {
        console.error('downloadAllLogs error:', error);
        ElMessage.error(t('device.logsDownloadFailed', { error: error.message || 'Unknown error' }));
      } finally {
        logDownloading.value = false;
      }
    };

    const handleLogSizeChange = () => {
      logCurrentPage.value = 1;
      fetchLogs(true);
    };

    const changeLogPage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      logCurrentPage.value = page;
      fetchLogs(true);
    };

    // 生命周期
    onMounted(() => {
      fetchDeviceList();
      startAutoRefresh();
    });

    onUnmounted(() => {
      stopAutoRefresh();
      stopLogAutoRefresh();
    });

    return {
      // 状态
      deviceList,
      loading,
      saving,
      deleting,
      testingDevice,
      searchKeyword,
      showDialog,
      showDeleteConfirm,
      currentDevice,
      deviceToDelete,
      isEditMode,
      errors,
      autoRefreshEnabled,
      
      // 计算属性
      filteredDevices,
      onlineCount,
      offlineCount,
      
      // 日志
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshLogEnabled,
      totalPages,
      
      // 方法
      showAddDialog,
      showEditDialog,
      closeDialog,
      saveDevice,
      confirmDelete,
      cancelDelete,
      deleteDevice,
      ConnectDevice,
      
      // 日志方法
      showLogDialog,
      closeLogDialog,
      toggleLogAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      changeLogPage
    };
  }
};
</script>

<style scoped>
.device-center-container {
  padding: 24px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8edf5 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 头部样式 */
.header-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
}

@media (min-width: 768px) {
  .header-section {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.header-content .page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.header-content .page-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.auto-refresh-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #52c41a;
  font-weight: 500;
}

.refresh-icon {
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.add-btn, .log-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.add-btn {
  background: linear-gradient(135deg, #1890ff, #096dd9);
}

.log-btn {
  background: linear-gradient(135deg, #722ed1, #531dab);
}

.add-btn:hover, .log-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* 统计卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 30px;
}

@media (min-width: 768px) {
  .stats-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-card.total {
  border-left: 4px solid #1890ff;
}

.stat-card.online {
  border-left: 4px solid #52c41a;
}

.stat-card.offline {
  border-left: 4px solid #ff4d4f;
}

.stat-icon {
  font-size: 36px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 12px;
}

.stat-content {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

/* 设备列表区域 */
.device-list-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

@media (min-width: 768px) {
  .section-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.search-box {
  margin-right: 80px;
  position: relative;
  width: 100%;
  max-width: 200px;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.search-input:focus {
  outline: none;
  border-color: #1890ff;
  background: white;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.3;
}

.empty-title {
  font-size: 20px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.empty-desc {
  color: #999;
  margin: 0 0 24px 0;
}

.empty-action-btn {
  padding: 12px 32px;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.empty-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.3);
}

/* 设备网格 */
.device-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 768px) {
  .device-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .device-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 设备卡片 */
.device-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.device-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.device-card.online {
  border-color: #52c41a;
}

.device-card.offline {
  border-color: #ff4d4f;
}

.device-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.device-status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot.online {
  background: #52c41a;
  box-shadow: 0 0 0 3px rgba(82, 196, 26, 0.2);
  animation: pulse 2s infinite;
}

.status-dot.offline {
  background: #ff4d4f;
  box-shadow: 0 0 0 3px rgba(255, 77, 79, 0.2);
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
}

.status-text {
  font-size: 12px;
  font-weight: 500;
}

.device-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.icon-btn:hover {
  background: #f8f9fa;
}

.edit-btn:hover {
  color: #1890ff;
}

.delete-btn:hover {
  color: #ff4d4f;
}

/* 设备信息 */
.device-info {
  flex: 1;
  margin-bottom: 16px;
}

.device-name {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.device-info p {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.5;
}

.info-label {
  color: #666;
  font-weight: 500;
  min-width: 60px;
  display: inline-block;
}

.info-value {
  color: #1a1a1a;
}

/* 设备底部 */
.device-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  gap: 8px;
}

.device-index {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

.index-label {
  color: #999;
}

.index-value {
  color: #666;
  font-weight: 500;
}

.test-btn, .connect-btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}

.test-btn {
  background: #f8f9fa;
  color: #1890ff;
}

.test-btn:hover:not(:disabled) {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.test-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.connect-btn {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
  border: none;
}

.connect-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.3);
}

/* 对话框 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.device-dialog, .confirm-dialog, .log-dialog {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

.log-dialog {
  max-width: 900px;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.log-header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
}

.refresh-label {
  color: #999;
}

.refresh-interval {
  color: #52c41a;
  font-size: 12px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #52c41a;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #ff4d4f;
}

.dialog-content {
  padding: 24px;
}

/* 表单样式 */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.form-input.error {
  border-color: #ff4d4f;
}

.form-input.error:focus {
  box-shadow: 0 0 0 3px rgba(255, 77, 79, 0.1);
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* 对话框按钮 */
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.cancel-btn {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #e0e0e0;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.save-btn {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.delete-confirm-btn {
  background: linear-gradient(135deg, #ff4d4f, #cf1322);
  color: white;
}

.delete-confirm-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3);
}

.delete-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 确认对话框 */
.confirm-dialog .dialog-content {
  text-align: center;
}

.confirm-dialog p {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 15px;
  line-height: 1.5;
}

.warning-text {
  color: #ff4d4f !important;
  font-weight: 500;
}

/* 日志对话框样式 */
.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-stats {
  font-size: 13px;
  color: #666;
}

.log-actions {
  display: flex;
  gap: 8px;
}

.log-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.download-btn {
  background: #f0f9eb;
  color: #52c41a;
}

.download-btn:hover:not(:disabled) {
  background: #52c41a;
  color: white;
}

.refresh-btn {
  background: #e6f7ff;
  color: #1890ff;
}

.refresh-btn:hover:not(:disabled) {
  background: #1890ff;
  color: white;
}

.clear-btn {
  background: #fff2f0;
  color: #ff4d4f;
}

.clear-btn:hover:not(:disabled) {
  background: #ff4d4f;
  color: white;
}

.log-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.empty-log .empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-list {
  padding: 12px;
}

.log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-item:last-child {
  border-bottom: none;
}

.log-item.log-level-error {
  background-color: #fef0f0;
}

.log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level.level-error {
  color: #f56c6c;
}

.log-level.level-warn {
  color: #e6a23c;
}

.log-level.level-info {
  color: #409eff;
}

.log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 4px 10px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #666;
}

.page-size-select {
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .log-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }

  .log-time {
    width: 100%;
    margin-bottom: 2px;
  }

  .log-message {
    width: 100%;
    margin-left: 0;
  }
}
</style>